/**
 * « Je rapporte mes bacs Folies » — panier et checkout.
 *
 * Décision de Philippe (25/09/2026, bacs reçus le 13/10) : le bac Folies est
 * VENDU 10 €, non consigné ; au retrait on échange un bac vide propre et en bon
 * état contre un bac plein (CGV art. 9). Le client annonce combien il en
 * rapporte pour que le préparateur remplisse ses bacs et que la caisse sache
 * combien en échanger. Rien n'est crédité ni déduit : c'est un échange.
 */
import { Link } from 'react-router-dom';
import { useState } from 'react';

interface Props {
  value: number;
  onChange: (n: number) => void;
  /** « panier » : bloc compact, sous les articles (page panier, ticket bureau). */
  variante?: 'checkout' | 'panier';
}

export default function BacsAnnonce({ value, onChange, variante = 'checkout' }: Props) {
  const compact = variante === 'panier';
  const [ouvert, setOuvert] = useState(value > 0);
  const regler = (n: number) => onChange(Math.max(0, Math.min(20, n)));

  return (
    <div className={compact ? 'shop-sans rounded-xl border border-[#4a5c3a]/25 bg-white p-3' : 'mt-8 border-t-2 border-gray-200 pt-6'}>
      <label className={`flex items-start cursor-pointer group ${compact ? 'gap-3' : 'gap-4'}`}>
        <input
          type="checkbox"
          checked={ouvert}
          onChange={(e) => {
            setOuvert(e.target.checked);
            onChange(e.target.checked ? Math.max(1, value) : 0);
          }}
          className={`${compact ? 'mt-0.5 w-5 h-5' : 'mt-1 w-6 h-6'} text-green-700 border-2 border-gray-300 rounded focus:ring-green-500 cursor-pointer flex-shrink-0`}
        />
        <div>
          <div className={`${compact ? 'text-[15px]' : 'text-xl'} font-bold text-gray-900 group-hover:text-green-700 transition-colors`}>
            🧺 Je rapporte mes bacs Folies (bacs bleus)
          </div>
          <p className={`text-gray-600 mt-1 ${compact ? 'text-[13px] leading-snug' : ''}`}>
            Seuls les{' '}
            <Link to="/shop?produit=BAC-FOLIES" className="underline font-semibold hover:text-green-700">bacs bleus marqués Folies</Link>{' '}
            s'échangent : rapportez-les vides et propres, on vous les échange un pour un contre vos bacs pleins.{' '}
            <strong>Ce n'est pas une consigne : rien n'est remboursé.</strong> Les caisses en carton ou en bois sont
            offertes : gardez-les ou rapportez-les, comme vous voulez.{' '}
            <Link to="/shop/cgv" className="underline hover:text-green-700">Conditions</Link>
          </p>
        </div>
      </label>

      {ouvert && (
        <div className={compact ? 'mt-3' : 'mt-4 ml-0 sm:ml-10'}>
          <div className={`flex items-center gap-3 rounded-xl border-2 px-3 py-2 ${value > 0 ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
            <div className={`min-w-0 flex-1 font-semibold text-gray-900 ${compact ? 'text-[13.5px] leading-tight' : ''}`}>
              Bacs vides que je rapporte
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={() => regler(value - 1)}
                disabled={value === 0}
                aria-label="Un bac de moins"
                className="w-11 h-11 rounded-lg border-2 border-gray-300 text-xl font-bold text-gray-700 disabled:opacity-30"
              >
                −
              </button>
              <span className="w-8 text-center text-lg font-bold tabular-nums">{value}</span>
              <button
                type="button"
                onClick={() => regler(value + 1)}
                disabled={value >= 20}
                aria-label="Un bac de plus"
                className="w-11 h-11 rounded-lg border-2 border-green-600 text-xl font-bold text-green-700 disabled:opacity-30"
              >
                +
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
